import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Input } from '@components/forms/Input';
import { Textarea } from '@components/forms/Textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { Select } from '@components/forms/Select';
import { AnonymousHeader } from '@components/layout/AnonymousHeader';
import { AnonymousFooter } from '@components/layout/AnonymousFooter';
import { authService } from './authService';
import { PlanSummary } from './PlanSummary';
import { usePlans } from './usePlans';
import type { ContractTerm } from './types';
import { useBrand } from '@contexts/BrandContext';
import { useConsent } from '@contexts/useConsent';
import type { ProductTier } from '../../types/shared';
import { getApiBaseUrl } from '@utils/apiClient';
import { formatPrice } from '@utils/formatPrice';
import { getMarketingPayload } from '@utils/marketing/attribution';
import { trackCompleteRegistration, trackPurchase } from '@utils/marketing/metaPixel';
import { isPostpaidTier, isProductTier } from '@utils/productTier';

interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  acceptedDpa: boolean;
  acceptedResellerAccess: boolean;

  verificationCode: string;

  // Step 3: Company
  orgName: string;
  industry: string;
  size: string;
  address: string;
  contactPhone: string;
  country: string;
  vatId: string;
  taxId: string;
  tier: ProductTier;
  contractTerm: ContractTerm;
  accountHolder: string;
  iban: string;
  sepaConsent: boolean;
}

const industries = [
  { value: '', label: 'Select Industry' },
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'real-estate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'other', label: 'Other' },
];

const companySizes = [
  { value: '', label: 'Select Company Size' },
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const countries = [
  { value: '', label: 'Select Country' },
  { value: 'Germany', label: 'Deutschland' },
  //{ value: 'Austria', label: 'Österreich' },
];

const tierOptions: { value: ProductTier; labelKey: string }[] = [
  { value: 'basic', labelKey: 'auth.tierBasic' },
  { value: 'business', labelKey: 'auth.tierBusiness' },
  { value: 'business_plus', labelKey: 'auth.tierBusinessPlus' },
  { value: 'enterprise', labelKey: 'auth.tierEnterprise' },
];

const contractTerms: ContractTerm[] = ['package_1', 'package_12', 'package_24'];

// Minimum first top-up of Basic (backend INITIAL_TOP_UP_MINIMUM).
const INITIAL_TOP_UP = 150;

// Tier and term pre-selected by a link such as ?plan=business_plus&duration=12.
// Unknown values are ignored; the customer can change both in step 3.
const planFromLink = (searchParams: URLSearchParams) => {
  const plan = searchParams.get('plan');
  const term = `package_${searchParams.get('duration')}` as ContractTerm;
  return {
    tier: isProductTier(plan) ? plan : ('basic' as ProductTier),
    contractTerm: contractTerms.includes(term) ? term : ('package_1' as ContractTerm),
  };
};

// Steps 3 and 4 show the plan on the left and the form on the right; the columns stack below 1280px.
const STEP_COLUMNS_CLASS = 'grid grid-cols-1 xl:grid-cols-5 gap-6 xl:gap-8';
const PLAN_COLUMN_CLASS =
  'xl:col-span-2 space-y-4 pb-6 border-b xl:pb-0 xl:border-b-0 xl:pr-8 xl:border-r border-gray-200 dark:border-gray-700';
const FORM_COLUMN_CLASS = 'xl:col-span-3 space-y-4';

interface SectionHeadingProps {
  id: string;
  icon: string;
  children: React.ReactNode;
}

const SectionHeading: React.FC<SectionHeadingProps> = ({ id, icon, children }) => (
  <h4
    id={id}
    className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white"
  >
    <span
      className="material-icons text-xl text-primary-600 dark:text-primary-400"
      aria-hidden="true"
    >
      {icon}
    </span>
    {children}
  </h4>
);

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const { trackingScope } = useConsent();
  const { data: plans, isLoading: plansLoading } = usePlans();

  // Capture referral code from URL (?ref=...) or WhiteLabel subdomain
  const referralCode = searchParams.get('ref') || brand.referral_code || '';
  // Ad origin and marketing consent for the server (Meta Conversions API)
  const marketingField = () => (trackingScope ? { marketing: getMarketingPayload() } : {});

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accessToken, setAccessToken] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const handledRedirectRef = useRef(false);

  // Handle redirect from login with payment_required
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const orgIdParam = searchParams.get('org_id');
    const sessionId = searchParams.get('session_id');

    // Don't process step=4 if we have session_id (Stripe redirect takes priority)
    if (sessionId) return;

    if (stepParam === '4' && orgIdParam) {
      // User was redirected from login - organization not active
      setCurrentStep(4);
      setOrganizationId(orgIdParam);
      // Get access token from localStorage (set by login)
      const token = authService.getAccessToken();
      if (token) {
        setAccessToken(token);
      }
    }
  }, [searchParams]);

  const [formData, setFormData] = useState<RegistrationData>(() => ({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
    acceptedDpa: false,
    acceptedResellerAccess: false,
    verificationCode: '',
    orgName: '',
    industry: '',
    size: '',
    address: '',
    contactPhone: '',
    country: '',
    vatId: '',
    taxId: '',
    ...planFromLink(searchParams),
    accountHolder: '',
    iban: '',
    sepaConsent: false,
  }));

  const postpaid = isPostpaidTier(formData.tier);
  const selectedPlan = plans?.find(plan => plan.tier === formData.tier);

  const handleInputChange = (field: keyof RegistrationData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError('');

    // Clear field errors for both camelCase and snake_case field names
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      // Clear camelCase error (e.g., 'vatId')
      delete newErrors[field];
      // Clear snake_case error (e.g., 'vat_id')
      const snakeCaseField = field.replace(/([A-Z])/g, '_$1').toLowerCase();
      delete newErrors[snakeCaseField];
      return newErrors;
    });
  };

  const validateStep1 = (): boolean => {
    if (!formData.firstName || formData.firstName.length < 2) {
      setError(t('validation.firstNameTooShort'));
      return false;
    }
    if (!formData.lastName || formData.lastName.length < 2) {
      setError(t('validation.lastNameTooShort'));
      return false;
    }
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError(t('validation.invalidEmail'));
      return false;
    }
    if (!formData.password || formData.password.length < 12) {
      setError(t('validation.passwordTooShort'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('validation.passwordsDoNotMatch'));
      return false;
    }
    if (!formData.acceptedTerms) {
      setError(t('validation.termsRequired'));
      return false;
    }
    if (!formData.acceptedDpa) {
      setError(t('validation.dpaRequired'));
      return false;
    }
    if (brand.is_whitelabel && !formData.acceptedResellerAccess) {
      setError(t('validation.resellerConsentRequired'));
      return false;
    }
    return true;
  };

  const handleStep1Submit = async () => {
    if (!validateStep1()) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await authService.register({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password1: formData.password,
        password2: formData.confirmPassword,
        accepted_terms: formData.acceptedTerms,
        accepted_dpa: formData.acceptedDpa,
        ...marketingField(),
      });

      // Store the access token for later use
      if (response.access) {
        setAccessToken(response.access);
      } else {
        setError(t('validation.registrationNoToken'));
        return;
      }

      // Move to email verification step
      setCurrentStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registrationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2Submit = async (code?: string) => {
    const codeToVerify = code || formData.verificationCode;
    if (!codeToVerify || codeToVerify.length !== 6) {
      setError(t('validation.verificationCodeRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await authService.verifyEmailCode(codeToVerify);
      setCurrentStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Email verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      await authService.resendEmailVerification(formData.email);
      // Show success message or toast
      setError(''); // Clear any existing errors
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  };

  const handleStep3Submit = async () => {
    if (
      !formData.orgName ||
      !formData.industry ||
      !formData.size ||
      !formData.address ||
      !formData.country
    ) {
      const missingFields = [];
      if (!formData.orgName) missingFields.push('Organization Name');
      if (!formData.industry) missingFields.push('Industry');
      if (!formData.size) missingFields.push('Company Size');
      if (!formData.address) missingFields.push('Address');
      if (!formData.country) missingFields.push('Country');
      setError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }

    if (postpaid && !formData.vatId.trim()) {
      setFieldErrors({ vat_id: t('auth.vatIdRequiredBusiness') });
      setError(t('auth.vatIdRequiredBusiness'));
      return;
    }

    // Check if we have an access token
    if (!accessToken) {
      setError(t('validation.accessTokenMissing'));
      return;
    }

    setIsLoading(true);
    setError('');
    setFieldErrors({});

    try {
      const result = await authService.createOrganization(
        {
          name: formData.orgName,
          industry: formData.industry,
          size: formData.size,
          vat_id: formData.vatId.trim() || '',
          tax_id: formData.taxId.trim() || '',
          contact_email: formData.email,
          contact_phone: formData.contactPhone.trim() || '',
          address: formData.address.trim() || '',
          description: '',
          country: formData.country.trim() || '',
          promo_code: '',
          product_tier: formData.tier,
          // Basic is month by month.
          contract_term: postpaid ? formData.contractTerm : 'package_1',
          ...(referralCode ? { referral_code: referralCode } : {}),
          ...(formData.acceptedResellerAccess ? { accepted_reseller_access: true } : {}),
          ...marketingField(),
        },
        accessToken
      );

      // Store organization ID for payment step; fall back to localStorage
      // (authService sets it there)
      const orgId = result.id || authService.getStoredOrganizationId();
      if (!orgId) {
        setError('Organization created but ID not found. Please try again.');
        return;
      }
      setOrganizationId(orgId);
      trackCompleteRegistration(orgId, formData.tier);

      // Postpaid tiers are invoice-billed — gather IBAN/SEPA mandate in step 4.
      // Move to payment step
      setCurrentStep(4);
    } catch (err) {
      if (err instanceof Error && 'fieldErrors' in err) {
        const errorWithFields = err as Error & { fieldErrors?: Record<string, unknown> };
        if (errorWithFields.fieldErrors) {
          const parsedFieldErrors: Record<string, string> = {};
          Object.entries(errorWithFields.fieldErrors).forEach(([field, fieldError]) => {
            let errorMessage = '';

            if (typeof fieldError === 'string') {
              errorMessage = fieldError;
            } else if (Array.isArray(fieldError) && fieldError.length > 0) {
              errorMessage = fieldError[0];
            } else if (typeof fieldError === 'object' && fieldError !== null) {
              const nestedError = fieldError as Record<string, unknown>;
              if (nestedError[field] && typeof nestedError[field] === 'string') {
                errorMessage = nestedError[field] as string;
              } else if (
                Array.isArray(nestedError[field]) &&
                (nestedError[field] as unknown[]).length > 0
              ) {
                errorMessage = (nestedError[field] as string[])[0];
              }
            }

            if (errorMessage) {
              parsedFieldErrors[field] = errorMessage;
            }
          });

          if (Object.keys(parsedFieldErrors).length > 0) {
            setFieldErrors(parsedFieldErrors);
            setError('');
          } else {
            setError(err.message);
          }
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : t('auth.organizationCreationError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBusinessMandateSubmit = async () => {
    if (!organizationId) {
      setError('Organization ID is missing. Please go back and complete organization setup.');
      return;
    }
    if (!formData.accountHolder.trim() || !formData.iban.trim()) {
      setError(t('auth.sepaFieldsRequired'));
      return;
    }
    if (!formData.sepaConsent) {
      setError(t('auth.sepaConsentRequired'));
      return;
    }

    setIsLoading(true);
    setError('');
    setFieldErrors({});

    try {
      await authService.submitSepaMandate(
        organizationId,
        {
          account_holder: formData.accountHolder.trim(),
          iban: formData.iban.trim(),
          consent: formData.sepaConsent,
        },
        accessToken
      );
      // The signed mandate is the first booking of a postpaid tier. The value
      // is the agreed monthly fee, as on the server.
      const monthlyFee = selectedPlan?.terms.find(
        term => term.contract_type === formData.contractTerm
      )?.agreed_monthly_fee;
      trackPurchase(
        organizationId,
        formData.tier,
        monthlyFee !== undefined ? Number(monthlyFee) : undefined
      );
      authService.clearTokens();
      setError(t('auth.registrationCompleteLogin'));
      navigate('/login');
    } catch (err) {
      const e = err as Error & { fieldErrors?: Record<string, unknown> };
      const ibanErr = e.fieldErrors?.iban;
      if (ibanErr) {
        setFieldErrors({ iban: Array.isArray(ibanErr) ? String(ibanErr[0]) : String(ibanErr) });
      }
      setError(e.message || 'SEPA mandate submission failed');
      setIsLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    if (postpaid) {
      return handleBusinessMandateSubmit();
    }
    if (!organizationId) {
      setError('Organization ID is missing. Please go back and complete organization setup.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Create Stripe checkout session for registration payment
      // Note: Users can enter coupon codes directly in Stripe Checkout
      const checkoutSession = await authService.createRegistrationPayment(
        organizationId,
        undefined,
        window.location.origin
      );

      // Clear authentication tokens before redirecting to Stripe
      // User will need to re-authenticate after payment
      authService.clearTokens();

      // Redirect to Stripe Checkout
      window.location.href = checkoutSession.checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment processing failed. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle Stripe Checkout redirect return
  useEffect(() => {
    const handleStripeRedirect = async () => {
      // Guard against double-invocation in Strict Mode
      if (handledRedirectRef.current) return;

      const sessionId = searchParams.get('session_id');
      const paymentStatus = searchParams.get('payment_status');

      // Process redirect if we have session_id (regardless of current step)
      if (!sessionId) return;

      handledRedirectRef.current = true;
      setIsLoading(true);
      setError('');

      // Tokens are already cleared before redirecting to Stripe (in handleFinalSubmit)
      // Just ensure they're cleared here as well (safety check)
      authService.clearTokens();

      // Clean up URL parameters immediately
      window.history.replaceState({}, '', '/register');

      try {
        // Check payment status from URL parameter
        if (paymentStatus === 'success' || paymentStatus === 'succeeded') {
          // Show success message and redirect to login
          setError(
            'Payment successful! Your account has been activated. Please log in to continue.'
          );
          setIsLoading(false);

          // Redirect to login immediately (no delay needed)
          navigate('/login', { replace: true });
        } else if (paymentStatus === 'canceled' || paymentStatus === 'failed') {
          setError(
            paymentStatus === 'canceled'
              ? 'Payment was canceled. Please log in and try again to complete your registration.'
              : 'Payment failed. Please log in and try again.'
          );
          setIsLoading(false);

          // Redirect to login immediately
          navigate('/login', { replace: true });
        } else {
          // No clear status - assume success
          setError('Payment processing completed. Please log in to continue.');
          setIsLoading(false);

          // Redirect to login immediately
          navigate('/login', { replace: true });
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to verify payment status. Please log in to continue.'
        );
        setIsLoading(false);

        // Redirect to login immediately
        navigate('/login', { replace: true });
      }
    };

    handleStripeRedirect();
  }, [searchParams, navigate]);

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label
            htmlFor="firstName"
            className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
          >
            {t('auth.firstName')} *
          </label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={e => handleInputChange('firstName', e.target.value)}
            placeholder={t('auth.enterFirstName')}
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <label
            htmlFor="lastName"
            className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
          >
            {t('auth.lastName')} *
          </label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={e => handleInputChange('lastName', e.target.value)}
            placeholder={t('auth.enterLastName')}
            required
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="email"
          className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
        >
          {t('auth.emailAddress')} *
        </label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={e => handleInputChange('email', e.target.value)}
          placeholder={t('auth.enterEmail')}
          required
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <label
          htmlFor="password"
          className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
        >
          {t('auth.password')} *
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={e => handleInputChange('password', e.target.value)}
            placeholder={t('auth.enterPassword')}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            disabled={isLoading}
          >
            <span className="material-icons text-lg">
              {showPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        </div>
        <div className="text-xs text-gray-500">{t('auth.passwordRequirements')}</div>
      </div>

      <div className="space-y-2">
        <label
          htmlFor="confirmPassword"
          className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
        >
          {t('auth.confirmPassword')} *
        </label>
        <div className="relative">
          <Input
            id="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={e => handleInputChange('confirmPassword', e.target.value)}
            placeholder={t('auth.confirmPasswordPlaceholder')}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            disabled={isLoading}
          >
            <span className="material-icons text-lg">
              {showConfirmPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2">
        <input
          id="acceptedTerms"
          type="checkbox"
          checked={formData.acceptedTerms}
          onChange={e => handleInputChange('acceptedTerms', e.target.checked)}
          disabled={isLoading}
          className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="acceptedTerms" className="block text-sm text-gray-700 dark:text-gray-300">
          {t('auth.acceptTerms')}{' '}
          <a
            href={`${getApiBaseUrl()}/organizations/legal/terms/`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('auth.termsAndConditions')}
          </a>
          {' *'}
        </label>
      </div>

      <div className="flex items-start gap-2">
        <input
          id="acceptedDpa"
          type="checkbox"
          checked={formData.acceptedDpa}
          onChange={e => handleInputChange('acceptedDpa', e.target.checked)}
          disabled={isLoading}
          className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="acceptedDpa" className="block text-sm text-gray-700 dark:text-gray-300">
          {t('auth.acceptDpa')}{' '}
          <a
            href={`${getApiBaseUrl()}/organizations/legal/dpa/`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            {t('auth.dataProcessingAgreement')}
          </a>
          {' *'}
        </label>
      </div>

      {brand.is_whitelabel && (
        <div className="flex items-start gap-2">
          <input
            id="acceptedResellerAccess"
            type="checkbox"
            checked={formData.acceptedResellerAccess}
            onChange={e => handleInputChange('acceptedResellerAccess', e.target.checked)}
            disabled={isLoading}
            className="h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <label
            htmlFor="acceptedResellerAccess"
            className="block text-sm text-gray-700 dark:text-gray-300"
          >
            {t('auth.acceptResellerAccess', { brandName: brand.brand_name })}{' '}
            {(brand.privacy_policy_url || brand.terms_url) && (
              <>
                {t('auth.furtherInfoIn')}{' '}
                {brand.terms_url && (
                  <a
                    href={brand.terms_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {t('auth.resellerTerms')}
                  </a>
                )}
                {brand.terms_url && brand.privacy_policy_url && ` ${t('auth.and')} `}
                {brand.privacy_policy_url && (
                  <a
                    href={brand.privacy_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {t('auth.resellerPrivacyPolicy')}
                  </a>
                )}
                {'.'}
              </>
            )}
            {' *'}
          </label>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
        <span className="material-icons text-2xl text-primary-600 dark:text-primary-400">
          email
        </span>
      </div>
      <div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('auth.verifyEmail')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {t('auth.verificationCodeSent')} <strong>{formData.email}</strong>
        </p>
        <p className="text-gray-600 dark:text-gray-400">{t('auth.enterVerificationCode')}</p>
      </div>

      <div className="flex justify-center space-x-2">
        {[1, 2, 3, 4, 5, 6].map(digit => (
          <Input
            key={digit}
            type="text"
            maxLength={1}
            className="w-12 h-12 text-center text-lg font-semibold"
            value={formData.verificationCode[digit - 1] || ''}
            onChange={e => {
              const newCode = formData.verificationCode.split('');
              newCode[digit - 1] = e.target.value;
              const updatedCode = newCode.join('');
              handleInputChange('verificationCode', updatedCode);

              // Auto-advance to next input if current digit is filled
              if (e.target.value && digit < 6) {
                const nextInput = document.querySelector(
                  `input[data-digit="${digit + 1}"]`
                ) as HTMLInputElement;
                if (nextInput) {
                  nextInput.focus();
                }
              }

              // Auto-verify when all 6 digits are entered
              if (updatedCode.length === 6) {
                setTimeout(() => handleStep2Submit(updatedCode), 100);
              }
            }}
            onKeyDown={e => {
              // Handle backspace to go to previous input
              if (e.key === 'Backspace' && !formData.verificationCode[digit - 1] && digit > 1) {
                const prevInput = document.querySelector(
                  `input[data-digit="${digit - 1}"]`
                ) as HTMLInputElement;
                if (prevInput) {
                  prevInput.focus();
                }
              }
            }}
            data-digit={digit}
            disabled={isLoading}
          />
        ))}
      </div>

      <div className="flex justify-center">
        <Button variant="outline" onClick={handleResendCode} disabled={isLoading || isResending}>
          {isResending ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
              {t('common.processing')}
            </div>
          ) : (
            t('auth.resendCode')
          )}
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('auth.companyInformation')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">{t('auth.tellUsAboutOrganization')}</p>
      </div>

      <div className={STEP_COLUMNS_CLASS}>
        <section aria-labelledby="register-plan-heading" className={PLAN_COLUMN_CLASS}>
          <SectionHeading id="register-plan-heading" icon="sell">
            {t('auth.planSection')}
          </SectionHeading>

          <div className="space-y-2">
            <label
              htmlFor="product-tier"
              className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
            >
              {t('auth.productTier')} *
            </label>
            <Select
              id="product-tier"
              value={formData.tier}
              onChange={value => handleInputChange('tier', Array.isArray(value) ? value[0] : value)}
              options={tierOptions.map(option => ({
                value: option.value,
                label: t(option.labelKey),
              }))}
              required
              disabled={isLoading}
              error={!!fieldErrors.product_tier}
              errorText={fieldErrors.product_tier}
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('auth.productTierHelp')}</p>
          </div>

          {postpaid && (
            <div className="space-y-2">
              <label
                htmlFor="contract-term"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.contractTerm')} *
              </label>
              <Select
                id="contract-term"
                value={formData.contractTerm}
                onChange={value =>
                  handleInputChange('contractTerm', Array.isArray(value) ? value[0] : value)
                }
                options={contractTerms.map(term => {
                  const label = t(`billing.contractType.${term}`);
                  const quote = selectedPlan?.terms.find(item => item.contract_type === term);
                  const discount = Number(quote?.discount_percent ?? 0);
                  return {
                    value: term,
                    label:
                      discount > 0
                        ? t('auth.contractTermDiscount', {
                            term: label,
                            discount: new Intl.NumberFormat(i18n.language).format(discount),
                          })
                        : label,
                  };
                })}
                required
                disabled={isLoading}
                error={!!fieldErrors.contract_term}
                errorText={fieldErrors.contract_term}
              />
            </div>
          )}

          <PlanSummary
            tier={formData.tier}
            contractTerm={formData.contractTerm}
            plan={selectedPlan}
            isLoading={plansLoading}
          />
        </section>

        <section aria-labelledby="register-company-heading" className={FORM_COLUMN_CLASS}>
          <SectionHeading id="register-company-heading" icon="business">
            {t('auth.companyDetails')}
          </SectionHeading>

          <div className="space-y-2">
            <label
              htmlFor="org-name"
              className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
            >
              {t('auth.organizationName')} *
            </label>
            <Input
              id="org-name"
              value={formData.orgName}
              onChange={e => handleInputChange('orgName', e.target.value)}
              placeholder={t('auth.enterOrganizationName')}
              required
              disabled={isLoading}
              state={fieldErrors.name ? 'error' : 'default'}
              errorText={fieldErrors.name}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="industry"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.industry')} *
              </label>
              <Select
                id="industry"
                value={formData.industry}
                onChange={value =>
                  handleInputChange('industry', Array.isArray(value) ? value[0] : value)
                }
                options={industries}
                required
                disabled={isLoading}
                error={!!fieldErrors.industry}
                errorText={fieldErrors.industry}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="company-size"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.companySize')} *
              </label>
              <Select
                id="company-size"
                value={formData.size}
                onChange={value =>
                  handleInputChange('size', Array.isArray(value) ? value[0] : value)
                }
                options={companySizes}
                required
                disabled={isLoading}
                error={!!fieldErrors.size}
                errorText={fieldErrors.size}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="address"
              className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
            >
              {t('auth.address')} *
            </label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={e => handleInputChange('address', e.target.value)}
              placeholder={t('auth.enterAddress')}
              maxLength={500}
              rows={3}
              required
              disabled={isLoading}
              state={fieldErrors.address ? 'error' : 'default'}
              errorText={fieldErrors.address}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="country"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.country')} *
              </label>
              <Select
                id="country"
                value={formData.country}
                onChange={value =>
                  handleInputChange('country', Array.isArray(value) ? value[0] : value)
                }
                options={countries}
                required
                disabled={isLoading}
                error={!!fieldErrors.country}
                errorText={fieldErrors.country}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="contact-phone"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.mobilePhone')}{' '}
                <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <Input
                id="contact-phone"
                type="tel"
                value={formData.contactPhone}
                onChange={e => handleInputChange('contactPhone', e.target.value)}
                placeholder={t('auth.enterMobilePhone')}
                maxLength={30}
                disabled={isLoading}
                state={fieldErrors.contact_phone ? 'error' : 'default'}
                errorText={fieldErrors.contact_phone}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="vat-id"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.vatId')}{' '}
                {postpaid ? '*' : <span className="text-gray-500 font-normal">(optional)</span>}
              </label>
              <Input
                id="vat-id"
                value={formData.vatId}
                onChange={e => {
                  const value = e.target.value.toUpperCase();
                  handleInputChange('vatId', value);
                }}
                placeholder={t('auth.enterVatId')}
                maxLength={50}
                pattern="[A-Z]{2}[0-9A-Z]{2,12}"
                disabled={isLoading}
                state={fieldErrors.vat_id ? 'error' : 'default'}
                errorText={fieldErrors.vat_id}
                helperText={!fieldErrors.vat_id ? t('auth.vatIdFormat') : undefined}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="tax-id"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.taxId')} <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <Input
                id="tax-id"
                value={formData.taxId}
                onChange={e => handleInputChange('taxId', e.target.value)}
                placeholder={t('auth.enterTaxId')}
                maxLength={50}
                disabled={isLoading}
                state={fieldErrors.tax_id ? 'error' : 'default'}
                errorText={fieldErrors.tax_id}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const renderPlanColumn = () => (
    <section aria-labelledby="register-plan-heading" className={PLAN_COLUMN_CLASS}>
      <SectionHeading id="register-plan-heading" icon="sell">
        {t('auth.planSection')}
      </SectionHeading>
      <PlanSummary
        tier={formData.tier}
        contractTerm={formData.contractTerm}
        plan={selectedPlan}
        isLoading={plansLoading}
      />
    </section>
  );

  const renderStep4Business = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('auth.sepaTitle')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">{t('auth.sepaSubtitle')}</p>
      </div>

      <div className={STEP_COLUMNS_CLASS}>
        {renderPlanColumn()}

        <section aria-labelledby="register-payment-heading" className={FORM_COLUMN_CLASS}>
          <SectionHeading id="register-payment-heading" icon="account_balance">
            {t('auth.bankDetails')}
          </SectionHeading>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="account-holder"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.accountHolder')} *
              </label>
              <Input
                id="account-holder"
                value={formData.accountHolder}
                onChange={e => handleInputChange('accountHolder', e.target.value)}
                placeholder={t('auth.enterAccountHolder')}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="iban"
                className="block text-sm font-semibold text-gray-900 dark:text-gray-300"
              >
                {t('auth.iban')} *
              </label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={e => handleInputChange('iban', e.target.value.toUpperCase())}
                placeholder="DE00 0000 0000 0000 0000 00"
                required
                disabled={isLoading}
                state={fieldErrors.iban ? 'error' : 'default'}
                errorText={fieldErrors.iban}
              />
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              id="sepa-consent"
              type="checkbox"
              checked={formData.sepaConsent}
              onChange={e => handleInputChange('sepaConsent', e.target.checked)}
              disabled={isLoading}
              className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <span>{t('auth.sepaMandateConsent')}</span>
          </label>

          <Button
            variant="gradient-multi"
            size="lg"
            onClick={handleFinalSubmit}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? 'Processing...' : t('auth.submitSepa')}
          </Button>
        </section>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t('auth.completeRegistration')}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">{t('auth.almostDone')}</p>
      </div>

      <div className={STEP_COLUMNS_CLASS}>
        {renderPlanColumn()}

        <section aria-labelledby="register-payment-heading" className={FORM_COLUMN_CLASS}>
          <SectionHeading id="register-payment-heading" icon="credit_card">
            {t('auth.payment')}
          </SectionHeading>

          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('auth.accountSetup')}:</span>
                <span className="font-semibold">{t('auth.novofonPlatform')}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('auth.minimumBalance')}:
                </span>
                <span className="font-bold text-primary-600 dark:text-primary-400">
                  {formatPrice(INITIAL_TOP_UP, i18n.language)}
                </span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="material-icons text-sm align-middle mr-1">info</span>
              {t('auth.promoCodeInCheckout')}
            </p>
          </div>

          <Button
            variant="gradient-multi"
            size="lg"
            onClick={handleFinalSubmit}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              <>
                <span className="material-icons mr-2">credit_card</span>
                {t('auth.payAndCompleteRegistration', {
                  amount: formatPrice(INITIAL_TOP_UP, i18n.language),
                })}
              </>
            )}
          </Button>
        </section>
      </div>
    </div>
  );

  const renderProgressSteps = () => (
    <div className="flex justify-between mb-8 w-full max-w-md mx-auto">
      {[1, 2, 3, 4].map(step => (
        <div key={step} className="flex flex-col items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
              step <= currentStep
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}
          >
            {step}
          </div>
          <div className="text-xs mt-1 text-center text-gray-900 dark:text-gray-300">
            {step === 1 && t('auth.personal')}
            {step === 2 && t('auth.email')}
            {step === 3 && t('auth.company')}
            {step === 4 && t('auth.payment')}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return postpaid ? renderStep4Business() : renderStep4();
      default:
        return renderStep1();
    }
  };

  const handleNext = () => {
    switch (currentStep) {
      case 1:
        handleStep1Submit();
        break;
      case 2:
        handleStep2Submit();
        break;
      case 3:
        handleStep3Submit();
        break;
      case 4:
        handleFinalSubmit();
        break;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col register-page">
      {/* Anonymous Header */}
      <AnonymousHeader />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className={`w-full ${currentStep >= 3 ? 'max-w-6xl' : 'max-w-2xl'} shadow-2xl`}>
          <CardHeader className="text-center pb-6">
            <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <span className="material-icons text-2xl text-white">person_add</span>
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('auth.registerTitle')}
            </CardTitle>
            <CardDescription className="text-lg text-gray-700 dark:text-gray-400">
              {t('auth.registerSubtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            {renderProgressSteps()}

            <form
              onSubmit={e => {
                e.preventDefault();
                handleNext();
              }}
              className="space-y-6"
            >
              {renderStepContent()}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Navigation Buttons */}
              {currentStep !== 4 && (
                <div className="flex justify-between pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={currentStep === 1 ? () => navigate('/login') : handleBack}
                    disabled={isLoading}
                  >
                    {currentStep === 1 ? t('auth.backToLogin') : t('common.back')}
                  </Button>

                  <Button type="submit" variant="gradient-multi" disabled={isLoading}>
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('common.processing')}
                      </div>
                    ) : (
                      t('common.continue')
                    )}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Anonymous Footer */}
      <AnonymousFooter />
    </div>
  );
};

export { Register };
